/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import api from '../../../services/api';

const DEFAULT_FORM = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export default function UserFormModal({
  open,
  onClose,
  userToEdit,
  onSaved,
}) {
  const isEditMode = !!userToEdit;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(DEFAULT_FORM);
      return;
    }
    if (userToEdit) {
      setForm({
        name: userToEdit.name || '',
        email: userToEdit.email || '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [open, userToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert('Nome é obrigatório.');
      return;
    }
    if (!form.email.trim()) {
      alert('E-mail é obrigatório.');
      return;
    }

    // Senha: no create é obrigatória; no edit é opcional (só troca se preencher).
    if (!isEditMode && !form.password) {
      alert('Senha é obrigatória.');
      return;
    }
    if (form.password && form.password !== form.confirmPassword) {
      alert('Senhas não são iguais!');
      return;
    }

    const data = {
      name: form.name.trim(),
      email: form.email.trim(),
      ...(form.password && { password: form.password }),
    };

    setSubmitting(true);
    try {
      const saved = isEditMode
        ? (await api.patch(`/users/${userToEdit.id}`, data)).data
        : (await api.post('/users', data)).data;
      alert(isEditMode ? 'Usuário atualizado com sucesso.' : 'Usuário cadastrado com sucesso.');
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 400) {
        alert('Senha inválida. A senha precisa ter:\n- Pelo menos uma letra maiúscula e uma minúscula;\n- Pelo menos um número e um caractere especial.');
      } else {
        alert(err.response?.data?.message || `Erro ao ${isEditMode ? 'atualizar' : 'cadastrar'} usuário.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEditMode ? 'Editar usuário' : 'Novo usuário';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {isEditMode
              ? 'Atualize os dados. Deixe a senha em branco pra manter a atual.'
              : 'Cadastre uma nova conta com acesso ao painel.'}
          </Typography>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Nome"
              type="text"
              name="name"
              value={form.name}
              placeholder="Nome do usuário"
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="E-mail"
              type="email"
              name="email"
              value={form.email}
              placeholder="usuario@exemplo.com"
              onChange={handleChange}
              required
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label={isEditMode ? 'Nova senha (opcional)' : 'Senha'}
              type="password"
              name="password"
              value={form.password}
              placeholder={isEditMode ? 'Deixe vazio pra manter a atual' : 'Mínimo 8 caracteres'}
              onChange={handleChange}
              required={!isEditMode}
            />
          </FormControl>

          <FormControl fullWidth sx={{ my: 1 }}>
            <TextField
              label="Confirmar senha"
              type="password"
              name="confirmPassword"
              value={form.confirmPassword}
              placeholder="Repita a senha"
              onChange={handleChange}
              required={!isEditMode || !!form.password}
            />
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={submitting}
          >
            {(() => {
              if (submitting) return 'Salvando...';
              return isEditMode ? 'Salvar alterações' : 'Cadastrar';
            })()}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

UserFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  userToEdit: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
};
